// Where a case is, from what has been recorded on it (not ActiveCampaign's stages, which mix steps with
// outcomes): received, acknowledged, assessed by QA, investigated, answered with the finding, closed. The
// step in hand shows its clock and who is next (Amit, 1 Nov 2026: "a visual guide that shows the
// lifecycle of the case", option 1). On a phone it is one line and a thin bar; the six steps from sm up.
import { byWhom, clockPhrase, when, type Attested, type ComplaintView } from './complaintsModel';

type Step = { key: string; label: string; done: boolean; detail: string | null; next: string };

/** Who recorded it, short: the person's name, or "recorded in ActiveCampaign" for a value read from the deal. */
const by = (a: Attested<unknown>) => (a.from === 'MIRROR' ? 'recorded in ActiveCampaign' : byWhom(a.from, a.byUpn));

export function caseSteps(c: ComplaintView): Step[] {
  const replied = !!(c.finding && c.lastReplyAt && new Date(c.lastReplyAt) > new Date(c.finding.at));
  return [
    { key: 'received', label: 'Received', done: true, detail: when(c.receivedAt), next: '' },
    { key: 'ack', label: 'Acknowledged', done: c.clocks.ackMet, detail: c.acknowledgedAt ? when(c.acknowledgedAt) : null, next: 'Customer service replies' },
    { key: 'assess', label: 'Assessed', done: !!c.severity, detail: c.severity ? `${c.severity.value}, ${by(c.severity)}` : null, next: 'QA sets the severity' },
    { key: 'investigate', label: 'Investigated', done: !!c.finding, detail: c.finding ? by(c.finding) : null, next: 'QA records the finding' },
    { key: 'answer', label: 'Finding sent', done: replied || !!c.closed, detail: replied && c.lastReplyAt ? when(c.lastReplyAt) : null, next: 'Customer service tells the consumer' },
    { key: 'close', label: 'Closed', done: !!c.closed, detail: c.closed ? when(c.closed.at) : null, next: c.severity?.value === 'High' ? 'QA closes it' : 'Customer service closes it' },
  ];
}

export function CaseProgress({ c }: { c: ComplaintView }) {
  const steps = caseSteps(c);
  const current = steps.findIndex((s) => !s.done);
  const clock = clockPhrase(c);
  // A pill on its own background: the tested pairs, so the clock reads in light and dark alike (axe, 1 Nov 2026).
  const tone = clock.tone === 'bad' ? 'bg-(--out-bg) text-(--out-fg)' : clock.tone === 'warn' ? 'bg-(--action-bg) text-(--action-fg)' : 'bg-(--subtle) text-(--fg)';
  const pill = <span className={`w-fit rounded-full px-2 text-[12px] font-semibold tabular-nums ${tone}`}>{clock.text}</span>;
  const now = current >= 0 ? steps[current] : null;
  return (
    <nav aria-label="Where this case is">
      {/* Phone: the step in hand, one line, and a bar of the six. */}
      <div className="flex flex-col gap-(--s1) sm:hidden">
        <p className="m-0 flex flex-wrap items-center gap-x-(--s2) text-(length:--fs-sm)">
          <span className="font-semibold">{now ? `${current + 1} of 6 · ${now.label}` : '6 of 6 · Closed'}</span>
          {now ? <span>{now.next}</span> : null}
          {pill}
        </p>
        <ol className="m-0 grid list-none grid-cols-6 gap-1 p-0" aria-hidden="true">
          {steps.map((s, i) => (
            <li key={s.key} className={`h-1.5 rounded-full ${s.done ? 'bg-(--in-fg)' : i === current ? 'bg-(--fg)' : 'bg-(--border-strong)'}`} />
          ))}
        </ol>
      </div>
      <ol className="m-0 hidden list-none grid-cols-3 gap-(--s2) p-0 sm:grid lg:grid-cols-6">
        {steps.map((s, i) => {
          const isNow = i === current;
          const ring = s.done ? 'border-(--in-line) bg-(--in-bg)' : isNow ? 'border-(--fg) bg-(--card)' : 'border-dashed border-(--border-strong) bg-transparent';
          return (
            <li key={s.key} aria-current={isNow ? 'step' : undefined} className={`flex flex-col gap-0.5 rounded-md border px-(--s3) py-(--s2) ${ring}`}>
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-(--muted)">
                {s.done ? '✓ ' : ''}
                {i + 1}. {s.label}
              </span>
              {s.done ? (
                <span className="text-[12px] tabular-nums">{s.detail}</span>
              ) : isNow ? (
                <>
                  <span className="text-[12px] font-semibold">{s.next}</span>
                  {pill}
                </>
              ) : (
                <span className="text-[12px] text-(--muted)">{s.next}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
